import React, {ReactElement, useCallback, useContext, useEffect, useMemo, useState} from 'react'
import {useTranslation} from 'react-i18next'
import {TitledBody} from '../../common/components/TitledBody'
import {HolidayForm, Schedule, Site} from '../../app/types'
import {Box, Checkbox, Divider, Grid, Paper, Step, StepLabel, Stepper, Typography} from "@mui/material";
import FormButtons from "../../common/form/FormButtons";
import SpinnableSubmitButton from "../../common/form/SpinnableSubmitButton";
import CancelButton from "../../common/form/CancelButton";
import {useHistory} from "react-router";
import {emptyPeriod} from "../../schedules/holidays/addHolidays";
import Form from "../../schedules/holidays/Form";
import ErrorAlert from "../../common/components/ErrorAlert";
import {api} from "../../common/api";
import {useUrlHelpers} from "../../common/util/hooks";
import {useSelector} from "react-redux";
import {sitesSelector} from "../../authentication/redux";
import {transformHolidayFormToSchedule} from "../../schedules/util/scheduleHelpers";
import {ColumnDefinition, InfiniteScrollTable} from "../../common/components/table/Table";
import {find, reject, uniqBy} from "ramda";
import {sorterHeader} from "../../common/components/table/SorterHeader";
import {useCustomerHolidaySchedules} from "../../common/servercall/useHolidayScheduleNames";


interface FormData  {
    siteIds: string[]
    holidayForm: HolidayForm
}

type ContextProps = {
    formData: FormData
    setFormData: (formData: FormData) => void
}


export const ScheduleImportContext = React.createContext<ContextProps>({
    formData: {
        siteIds: [],
        holidayForm: {
            uuid: '',
            name: '',
            type: 'HOLIDAY',
            holidayPeriods: [emptyPeriod],
        }
    },
    setFormData: (formData: FormData) => {}
})

export const ScheduleImport = () => {
    const history = useHistory()

    const {createUrl} = useUrlHelpers();
    const [activeStep, setActiveStep] = useState<number>(0);
    const [submitting, setSubmitting] = useState<boolean>(false)
    const [submitError, setSubmitError] = useState<any>(null)
    const [atLeastOneSiteSelected, setAtLeastOneSiteSelected] = useState<boolean>(false)
    const [formData, setFormData] = useState<FormData>({
        siteIds: [],
        holidayForm: {
            uuid: '',
            name: '',
            type: 'HOLIDAY',
            holidayPeriods: [emptyPeriod],
        }
    })

    const { t } = useTranslation()

    const handleNext = useCallback(async () => {
        if (activeStep === 2) {
            try {
                setSubmitError(null)
                setSubmitting(true)

                await api.put(createUrl('import-holidays'), {
                    siteIds: formData.siteIds,
                    schedule: transformHolidayFormToSchedule(formData.holidayForm)
                })
                setSubmitting(false)
                history.push('/voicecloud-sites')
            } catch (error) {
                setSubmitting(false)
                setSubmitError(error)
            }
        } else {
            setActiveStep((prevActiveStep) => prevActiveStep + 1);
        }
    }, [activeStep, history, formData, createUrl]);


    const handleBack = () => {
        setSubmitError(null)
        setActiveStep((prevActiveStep) => prevActiveStep - 1);
    };

    const titles = useMemo(() =>
        ['holidays-import.step-1', 'holidays-import.step-2', 'holidays-import.step-3'].map((key, index) => `${index + 1}. ${t(key)}`)
    , [t])

    const buildFormButtons = useCallback((isSubmit: boolean, isSubmitting: boolean) => {
        return <>
            <Grid item >
                <Divider/>
            </Grid>
            <Grid item mt={3} >
                <FormButtons buttons={[<CancelButton
                    title= {t("general.back")}
                    disabled={activeStep === 0}
                    onClick={handleBack}
                />,
                    <SpinnableSubmitButton disabled={!atLeastOneSiteSelected}  showSpinner={isSubmitting || submitting} label={activeStep === titles.length -1 ? t("general.confirm") : t("general.next")} onClick={isSubmit ? undefined : handleNext}/>
                ]} fullWidth={true}/>
            </Grid>
        </>
    }, [activeStep, atLeastOneSiteSelected, t, titles.length, submitting, handleNext])

    const Comps = useMemo(() =>
        <ScheduleImportContext.Provider value={{ formData, setFormData }}>
            <Box hidden={activeStep !== 0} key={0}>
                <Step1Comp allowToContinueChanged={setAtLeastOneSiteSelected} buildFormButtons={buildFormButtons} next={handleNext} submitError={submitError} />
            </Box>
            <Box hidden={activeStep !== 1} key={1}>
                <Step2Comp allowToContinueChanged={(allowToContinue: boolean) => {}} buildFormButtons={buildFormButtons} next={handleNext} submitError={submitError} />
            </Box>
            <Box hidden={activeStep !== 2} key={2}>
                <Step3Comp allowToContinueChanged={(allowToContinue: boolean) => {}} buildFormButtons={buildFormButtons} next={handleNext} submitError={submitError} />
            </Box>
        </ScheduleImportContext.Provider>, [formData, setFormData, activeStep, buildFormButtons, handleNext, submitError])


    return (
        <>
            <TitledBody title={t('holidays-import.title')} >
                <Grid container flexWrap={'nowrap'} >
                    <Grid xs={10} container item justifyContent={'space-between'} flexDirection={'column'} spacing={1} flexWrap={'nowrap'}>
                        <Paper elevation={2} sx={{ p: 4, mr: 4}} square={false}>
                            <Grid rowSpacing={4} item container flexDirection={'column'} sx={{height: '100%'}} justifyContent={'flex-end'}  flexWrap={'nowrap'}>
                                <Grid item sx={{mt: 3, mb: 2}}>
                                    <Typography variant='h6' align='left' gutterBottom>
                                        {titles[activeStep]}
                                    </Typography>
                                </Grid>
                                {Comps}
                            </Grid>
                        </Paper>
                    </Grid>
                    <Grid item  >
                        <Stepper activeStep={activeStep} orientation="vertical">
                            {titles.map((title, index) => {
                                return (
                                    <Step key={title} >
                                        <StepLabel >{title}</StepLabel>
                                    </Step>
                                );
                            })}
                        </Stepper>
                    </Grid>
                </Grid>
            </TitledBody>
        </>
    )
}

interface StepProps {
    allowToContinueChanged: ( allowToContinue: boolean) => void
    buildFormButtons: (isSubmit: boolean, isSubmitting: boolean) => ReactElement
    next: () => void
    submitError: any
}

const Step1Comp = (entities: StepProps) => {
    const { t } = useTranslation()
    const [allSelected, setAllSelected] = useState<boolean>(false)

    const { formData, setFormData } = useContext(ScheduleImportContext);
    const availableSites = useSelector(sitesSelector)

    useEffect(() => {
        entities.allowToContinueChanged(formData.siteIds.length > 0)
    }, [formData.siteIds, entities]);

    const allSelectedChanged = useCallback((selectAll: boolean) => {
        setAllSelected(selectAll)
        if (selectAll) {
            setFormData({...formData, siteIds: availableSites?.map((site: Site) => site.productId) || []})
        } else {
            setFormData({...formData, siteIds: []})
        }
    }, [setAllSelected, formData, setFormData, availableSites])

    const addOrRemoveEntryToList = useCallback((entry: Site) => (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.checked) {
            setFormData({...formData, siteIds: [...formData.siteIds, entry.productId]})
        } else {
            setFormData({...formData, siteIds: reject((v: string) => v === entry.productId, formData.siteIds)})
        }
    }, [formData, setFormData])


    const columns: ColumnDefinition<Site>[] = useMemo(
        () => [
            {
                title: '',
                checkbox: {selected: allSelected, setSelected: allSelectedChanged},
                key: 'productId',
                sorter: false,
                style: {width: '1rem'},
                render: (text: string, record: Site) => <>{<Checkbox onChange={addOrRemoveEntryToList(record)}
                                                                          checked={!!find(entry => entry === text, formData.siteIds)}/>}</>,
            },
            {
                title: t('enterprise-sites.table.name'),
                key: 'name',
                sorter: false,
                render: (text: string, record) => <>{text}</>,
            },
            {
                title: 'Alias',
                key: 'alias',
                sorter: false,
                render: (text: string, record) => <>{text}</>,
            }
            ], [t, formData.siteIds, allSelected, allSelectedChanged, addOrRemoveEntryToList]);

    const sorter = useCallback(col => sorterHeader(col, null, () => {}), [])

    return (
        <Grid container item flexDirection={'column'}>
            <Grid item >
                <InfiniteScrollTable<Site>
                    hasNextPage={false}
                    data={availableSites || []}
                    fetchNextPage={() => {}}
                    isFetching={false}
                    getRowKey={site => site.productId}
                    sorter={sorter}
                    columns={columns}
                    tableId={'sites-table'}
                    size={'small'}
                />
            </Grid>
            {entities.buildFormButtons(false, false)}
        </Grid>
    )
}

const Step2Comp = (entities: StepProps) => {
    const { formData, setFormData } = useContext(ScheduleImportContext);

    const {schedules} = useCustomerHolidaySchedules()


    const existingHolidayOptions = useMemo(() => {
        return {
            getOptions: async (searchTerm: string) => uniqBy(it => it.name, schedules),
            getOptionLabel: (item: any) => {
                return typeof item === 'string' ? item || '' : item.name
            },
            getOptionValue: (item: any) => {
                return typeof item === 'string' ? item : item.name
            },
            isOptionEqualToValue: (option: Schedule, value: Schedule) => option?.uuid === value?.uuid,
            filterOnServer: false,
            defaultObject: '',
            freeText: true,
        }
    }, [schedules])

    const onSubmit= useCallback(async (holidayForm: HolidayForm) => {
        setFormData({...formData, holidayForm: holidayForm})
        entities.next()
    }, [entities, setFormData, formData])

    return (
        <Form onSubmit={onSubmit} holidays={formData.holidayForm}
              autoCompleteOptionsForName={existingHolidayOptions}
              buildFormButtons={isSubmitting => entities.buildFormButtons(true, isSubmitting)} />
    )
}

const Step3Comp = (entities: StepProps) => {
    const { t } = useTranslation()

    const { formData } = useContext(ScheduleImportContext);

    const errorBody = useCallback((errors: any) => {
        return <>{t('general.unknown-server-error')}</>
    }, [t])

    const ErrorBox = useMemo(() => {
        if (!!entities.submitError) {
            return () => <ErrorAlert showAlert={true} body={errorBody(entities.submitError)} />
        } else {
            return () => <></>
        }
    }, [entities.submitError, errorBody])

    return (
        <>
            <Grid item xs={'auto'} spacing={2} mb={2} mt={2}>
                <ErrorBox/>
                <Typography variant={"body1"}>
                    {t('holidays-import.confirmation-text', {name: formData.holidayForm.name})}
                </Typography>
            </Grid>
            {entities.buildFormButtons(false, false)}
        </>
    )
}


